<template>
  <layout-page>
    <section-page title="Campaigns" full-height>
      <template v-slot:right>
        <campaign-filters />
        <filter-button-add @click="openNewCampaignModal()"
          >New Campaign</filter-button-add
        >
      </template>

      <app-loading-section :loading="loading">
        <app-empty-message :count="campaigns.length" :heading="filterMessage">
          <table-default :table-data="campaigns" default-sort="label">
            <b-table-column label="Name" field="label" sortable v-slot="props"
              >{{ props.row.label }}
            </b-table-column>

            <b-table-column
              label="Units Label"
              field="units_label"
              sortable
              v-slot="props"
              >{{ props.row.units_label }}
            </b-table-column>

            <b-table-column
              label="Channel"
              field="channel"
              sortable
              v-slot="props"
              >{{ getChannelLabel(props.row.channel) }}
            </b-table-column>

            <b-table-column
              label="Status"
              field="deleted_at"
              sortable
              v-slot="props"
              >{{ props.row.deleted_at ? "Archived" : "Active" }}
            </b-table-column>

            <b-table-column
              label="Phone Number"
              field="integrationPhoneNumber"
              sortable
              v-slot="props"
              >{{
                props.row.integrationPhoneNumber
                  ? `${props.row.integrationPhoneNumber.label}: ${$utils.formatNumber(props.row.integrationPhoneNumber.number)}`
                  : "--"
              }}
            </b-table-column>

            <b-table-column
              label="Cost"
              field="total_costs"
              numeric
              width="100"
              sortable
              v-slot="props"
              >{{ formatCurrency(props.row.total_costs) }}
            </b-table-column>

            <b-table-column
              label="Leads"
              field="record_count"
              width="80"
              numeric
              sortable
              v-slot="props"
              >{{ props.row.record_count }}
            </b-table-column>

            <b-table-column cell-class="action_cell" width="120" v-slot="props">
              <button-link @click="openEditCampaignModal(props.row)"
                >Manage</button-link
              >
            </b-table-column>
          </table-default>
        </app-empty-message>
      </app-loading-section>
    </section-page>

    <modal-container>
      <modal-crud-form
        ref="campaignCreateModal"
        title="Campaign"
        form-component="campaign-form"
        @saved="reloadCampaigns"
      />

      <modal-form-edit
        ref="campaignEditModal"
        title="Campaign"
        form-component="campaign-form"
        :hide-delete-button="showRestoreButton"
        :show-restore-button="showRestoreButton"
        :onClosePrompt="resetActiveCampaign"
        @updated="reloadCampaigns"
        @deleted="reloadCampaigns"
        @restored="reloadCampaigns"
      >
        <campaign-costs />
        <campaign-unit-counts :campaign-id="activeCampaign.id" />
      </modal-form-edit>
    </modal-container>
  </layout-page>
</template>

<script>
import CampaignFilters from "./components/CampaignFilters";
import CampaignCosts from "./components/CampaignCosts";
import CampaignUnitCounts from "./components/CampaignUnitCounts";
import { onMounted, ref, computed, onUnmounted } from "vue";
import { useCampaignStore } from "./campaigns.store";
import utils from "../../services/utils";
import { ENUMS } from "../../services/enums";

export default {
  name: "CampaignsPage",
  metaInfo: {
    title: "Campaigns",
  },
  components: {
    CampaignCosts,
    CampaignUnitCounts,
    CampaignFilters,
  },
  setup() {
    const campaignStore = useCampaignStore();
    const loading = ref(true);

    onMounted(() => {
      campaignStore.loadAllCampaigns().then(() => {
        loading.value = false;
      });
    });

    onUnmounted(() => {
      campaignStore.resetStore();
    });

    const campaigns = computed(() => campaignStore.filteredCampaigns);
    const activeCampaign = computed(() => campaignStore.activeCampaign);
    const filterMessage = computed(() => campaignStore.filteredMessage);

    const showRestoreButton = computed(() => {
      return activeCampaign.value.deleted_at !== null;
    });

    function getChannelLabel(channel) {
      return utils.newGetEnumLabel(channel, ENUMS.CampaignChannelEnum);
    }

    function formatCurrency(value) {
      return utils.formatCurrency(value);
    }

    function openNewCampaignModal() {
      campaignStore.resetActiveCampaign();
      this.$refs.campaignCreateModal.open();
    }

    function openEditCampaignModal(campaign) {
      campaignStore.setActiveCampaign(campaign);
      this.$refs.campaignEditModal.open(campaign);
    }

    function resetActiveCampaign() {
      campaignStore.resetActiveCampaign();
    }

    function reloadCampaigns() {
      loading.value = true;
      campaignStore.loadAllCampaigns().then(() => {
        loading.value = false;
      });
    }

    return {
      loading,
      campaigns,
      activeCampaign,
      filterMessage,
      showRestoreButton,
      getChannelLabel,
      formatCurrency,
      openNewCampaignModal,
      openEditCampaignModal,
      resetActiveCampaign,
      reloadCampaigns,
    };
  },
};
</script>
